import { useMemo } from 'react';

/**
 * PageHero — responsive editorial hero image.
 *
 * Mobile (< md): renders a subject-focused portrait crop (aspect-[4/5] + object-cover + object-top)
 * so the face stays anchored at the top and no letterbox appears.
 * Tablet + Desktop (md+): renders a full-bleed landscape crop (object-cover + object-center)
 * with the container's existing fixed height (h-[70vh] lg:h-[86vh]) preserved.
 *
 * Props:
 *   src              — image URL
 *   alt              — alt text (required for a11y)
 *   mobileAnchor     — 'top' | 'center' | 'bottom' | 'left' (mobile focal point). default 'top'
 *   desktopAnchor    — 'center' | 'top' | 'left' | 'right' (desktop focal point). default 'center'
 *   desktopHeight    — Tailwind height on md+ (e.g., 'md:h-[70vh] lg:h-[86vh]'). default 'md:h-[70vh] lg:h-[86vh]'
 *   mobileAspect     — Tailwind aspect on <md (e.g., 'aspect-[4/5]'). default 'aspect-[4/5]'
 *   rounded          — extra classes for container. default 'rounded-3xl'
 *   testId           — data-testid override
 *   priority         — if true, sets fetchpriority=high + eager loading (for LCP)
 */
export default function PageHero({
  src,
  alt,
  mobileAnchor = 'top',
  desktopAnchor = 'center',
  desktopHeight = 'md:h-[70vh] lg:h-[86vh]',
  mobileAspect = 'aspect-[4/5]',
  rounded = 'rounded-3xl',
  testId,
  priority = false,
  className = '',
}) {
  const anchorMap = {
    top: 'object-top',
    center: 'object-center',
    bottom: 'object-bottom',
    left: 'object-left',
    right: 'object-right',
    'left-top': 'object-left-top',
    'left-bottom': 'object-left-bottom',
  };
  const mobileClass = anchorMap[mobileAnchor] || 'object-top';
  const desktopClass = `md:${anchorMap[desktopAnchor] || 'object-center'}`;

  const imgClasses = useMemo(
    () =>
      `w-full ${mobileAspect} md:aspect-auto ${desktopHeight} object-cover ${mobileClass} ${desktopClass} ${className}`.trim(),
    [mobileAspect, desktopHeight, mobileClass, desktopClass, className]
  );

  return (
    <div className={`${rounded} overflow-hidden luxury-shadow`} data-testid={testId}>
      <img
        src={src}
        alt={alt}
        className={imgClasses}
        loading={priority ? 'eager' : 'lazy'}
        {...(priority ? { fetchpriority: 'high' } : {})}
      />
    </div>
  );
}
